/**
 * Dog Breed Profiles for Trail Matching
 *
 * Each breed is profiled with characteristics that map to trail traits:
 *
 * Trail traits:
 *   dogTraffic (1-100)  — High traffic → Low traffic
 *   footTraffic (1-100) — High traffic → Low traffic
 *   paths (1-100)       — Open wide → Narrow paths
 *   exposure (1-100)     — Shaded → Exposed
 *   offLeash (bool)     — Off-leash friendly
 *   wildlife (bool)     — Wildlife/livestock present
 *
 * Dog characteristics:
 *   size               — "tiny" | "small" | "medium" | "large" | "giant"
 *   energyLevel        — 1-100: how energetic the breed is overall
 *   exerciseNeeds      — 1-100: daily exercise requirement intensity
 *   heatTolerance      — 1-100: ability to handle hot/exposed conditions
 *   coldTolerance      — 1-100: ability to handle cold/wet conditions
 *   dogFriendliness    — 1-100: sociability with other dogs (maps to dogTraffic)
 *   peopleFriendliness — 1-100: sociability with strangers (maps to footTraffic)
 *   trainability       — 1-100: obedience & recall reliability (maps to offLeash)
 *   preyDrive          — 1-100: chase instinct (maps to wildlife)
 *   stamina            — 1-100: endurance for long hikes (maps to distance/elevation)
 */

export type DogSize = "tiny" | "small" | "medium" | "large" | "giant";

export interface BreedProfile {
  size: DogSize;
  energyLevel: number;
  exerciseNeeds: number;
  heatTolerance: number;
  coldTolerance: number;
  dogFriendliness: number;
  peopleFriendliness: number;
  trainability: number;
  preyDrive: number;
  stamina: number;
}

export type BreedProfiles = Record<string, BreedProfile>;

const dogBreeds: BreedProfiles = {
  "Affenpinscher": {
    size: "small",
    energyLevel: 55,
    exerciseNeeds: 40,
    heatTolerance: 35,
    coldTolerance: 30,
    dogFriendliness: 40,
    peopleFriendliness: 45,
    trainability: 40,
    preyDrive: 50,
    stamina: 35,
  },
  "Afghan Hound": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 60,
    coldTolerance: 35,
    dogFriendliness: 35,
    peopleFriendliness: 30,
    trainability: 25,
    preyDrive: 80,
    stamina: 65,
  },
  "Airedale Terrier": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 50,
    coldTolerance: 50,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 60,
    stamina: 65,
  },
  "Akita": {
    size: "large",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 30,
    coldTolerance: 85,
    dogFriendliness: 25,
    peopleFriendliness: 30,
    trainability: 40,
    preyDrive: 65,
    stamina: 55,
  },
  "Alano Español": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 75,
    heatTolerance: 70,
    coldTolerance: 35,
    dogFriendliness: 45,
    peopleFriendliness: 50,
    trainability: 50,
    preyDrive: 70,
    stamina: 70,
  },
  "Alaskan Klee Kai": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 20,
    coldTolerance: 90,
    dogFriendliness: 35,
    peopleFriendliness: 30,
    trainability: 45,
    preyDrive: 50,
    stamina: 45,
  },
  "Alaskan Malamute": {
    size: "giant",
    energyLevel: 70,
    exerciseNeeds: 75,
    heatTolerance: 15,
    coldTolerance: 95,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 35,
    preyDrive: 60,
    stamina: 85,
  },
  "American Bulldog": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 40,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 45,
    preyDrive: 55,
    stamina: 55,
  },
  "American English Coonhound": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 55,
    coldTolerance: 40,
    dogFriendliness: 60,
    peopleFriendliness: 60,
    trainability: 40,
    preyDrive: 85,
    stamina: 80,
  },
  "American Eskimo Dog": {
    size: "small",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 20,
    coldTolerance: 85,
    dogFriendliness: 60,
    peopleFriendliness: 55,
    trainability: 60,
    preyDrive: 35,
    stamina: 45,
  },
  "American Foxhound": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 50,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 60,
    trainability: 35,
    preyDrive: 85,
    stamina: 80,
  },
  "American Hairless Terrier": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 50,
    heatTolerance: 20,
    coldTolerance: 10,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 55,
    stamina: 35,
  },
  "American Leopard Hound": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 55,
    coldTolerance: 45,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 50,
    preyDrive: 80,
    stamina: 75,
  },
  "American Staffordshire Terrier": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 35,
    dogFriendliness: 40,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 50,
    stamina: 60,
  },
  "American Water Spaniel": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 60,
    preyDrive: 55,
    stamina: 65,
  },
  "Anatolian Shepherd Dog": {
    size: "giant",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 55,
    coldTolerance: 55,
    dogFriendliness: 25,
    peopleFriendliness: 20,
    trainability: 30,
    preyDrive: 70,
    stamina: 65,
  },
  "Appenzeller Sennenhund": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 35,
    coldTolerance: 75,
    dogFriendliness: 50,
    peopleFriendliness: 40,
    trainability: 55,
    preyDrive: 50,
    stamina: 70,
  },
  "Aussiedoodle": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 70,
    peopleFriendliness: 70,
    trainability: 75,
    preyDrive: 35,
    stamina: 65,
  },
  "Australian Cattle Dog": {
    size: "medium",
    energyLevel: 90,
    exerciseNeeds: 85,
    heatTolerance: 65,
    coldTolerance: 45,
    dogFriendliness: 40,
    peopleFriendliness: 40,
    trainability: 65,
    preyDrive: 60,
    stamina: 85,
  },
  "Australian Kelpie": {
    size: "medium",
    energyLevel: 95,
    exerciseNeeds: 90,
    heatTolerance: 65,
    coldTolerance: 40,
    dogFriendliness: 45,
    peopleFriendliness: 40,
    trainability: 65,
    preyDrive: 65,
    stamina: 90,
  },
  "Australian Shepherd": {
    size: "medium",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 75,
    preyDrive: 55,
    stamina: 75,
  },
  "Australian Stumpy Tail Cattle Dog": {
    size: "medium",
    energyLevel: 90,
    exerciseNeeds: 85,
    heatTolerance: 65,
    coldTolerance: 45,
    dogFriendliness: 40,
    peopleFriendliness: 40,
    trainability: 60,
    preyDrive: 60,
    stamina: 85,
  },
  "Australian Terrier": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 50,
    heatTolerance: 45,
    coldTolerance: 40,
    dogFriendliness: 45,
    peopleFriendliness: 50,
    trainability: 55,
    preyDrive: 55,
    stamina: 40,
  },
  "Azawakh": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 85,
    coldTolerance: 15,
    dogFriendliness: 30,
    peopleFriendliness: 25,
    trainability: 30,
    preyDrive: 80,
    stamina: 70,
  },
  "Barbado da Terceira": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 55,
    coldTolerance: 45,
    dogFriendliness: 60,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 45,
    stamina: 60,
  },
  "Barbet": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 55,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 60,
    preyDrive: 40,
    stamina: 60,
  },
  "Basenji": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 65,
    coldTolerance: 25,
    dogFriendliness: 30,
    peopleFriendliness: 35,
    trainability: 30,
    preyDrive: 80,
    stamina: 55,
  },
  "Bassador": {
    size: "medium",
    energyLevel: 50,
    exerciseNeeds: 45,
    heatTolerance: 35,
    coldTolerance: 45,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 45,
    preyDrive: 50,
    stamina: 40,
  },
  "Basset Fauve de Bretagne": {
    size: "small",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 45,
    preyDrive: 65,
    stamina: 45,
  },
  "Basset Hound": {
    size: "large",
    energyLevel: 30,
    exerciseNeeds: 30,
    heatTolerance: 25,
    coldTolerance: 30,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 30,
    preyDrive: 65,
    stamina: 25,
  },
  "Bavarian Mountain Scent Hound": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 65,
    dogFriendliness: 40,
    peopleFriendliness: 35,
    trainability: 55,
    preyDrive: 85,
    stamina: 70,
  },
  "Beabull": {
    size: "medium",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 30,
    coldTolerance: 30,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 40,
    preyDrive: 40,
    stamina: 35,
  },
  "Beagle": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 40,
    dogFriendliness: 80,
    peopleFriendliness: 80,
    trainability: 35,
    preyDrive: 85,
    stamina: 55,
  },
  "Beagle Mix": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 45,
    coldTolerance: 40,
    dogFriendliness: 75,
    peopleFriendliness: 75,
    trainability: 40,
    preyDrive: 70,
    stamina: 50,
  },
  "Bearded Collie": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 35,
    coldTolerance: 65,
    dogFriendliness: 70,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 40,
    stamina: 65,
  },
  "Beauceron": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 35,
    peopleFriendliness: 40,
    trainability: 60,
    preyDrive: 65,
    stamina: 75,
  },
  "Bedlington Terrier": {
    size: "small",
    energyLevel: 60,
    exerciseNeeds: 50,
    heatTolerance: 40,
    coldTolerance: 40,
    dogFriendliness: 50,
    peopleFriendliness: 55,
    trainability: 50,
    preyDrive: 60,
    stamina: 45,
  },
  "Belgian Laekenois": {
    size: "medium",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 45,
    coldTolerance: 55,
    dogFriendliness: 35,
    peopleFriendliness: 35,
    trainability: 65,
    preyDrive: 60,
    stamina: 80,
  },
  "Belgian Malinois": {
    size: "large",
    energyLevel: 95,
    exerciseNeeds: 95,
    heatTolerance: 50,
    coldTolerance: 50,
    dogFriendliness: 35,
    peopleFriendliness: 40,
    trainability: 85,
    preyDrive: 65,
    stamina: 90,
  },
  "Belgian Sheepdog": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 35,
    coldTolerance: 65,
    dogFriendliness: 45,
    peopleFriendliness: 40,
    trainability: 70,
    preyDrive: 55,
    stamina: 80,
  },
  "Belgian Tervuren": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 40,
    coldTolerance: 60,
    dogFriendliness: 45,
    peopleFriendliness: 40,
    trainability: 70,
    preyDrive: 60,
    stamina: 80,
  },
  "Bergamasco Sheepdog": {
    size: "large",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 35,
    coldTolerance: 70,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 45,
    preyDrive: 35,
    stamina: 55,
  },
  "Berger Picard": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 55,
    preyDrive: 50,
    stamina: 70,
  },
  "Bernedoodle": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 30,
    coldTolerance: 70,
    dogFriendliness: 75,
    peopleFriendliness: 75,
    trainability: 70,
    preyDrive: 25,
    stamina: 60,
  },
  "Bernese Mountain Dog": {
    size: "large",
    energyLevel: 55,
    exerciseNeeds: 55,
    heatTolerance: 20,
    coldTolerance: 90,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 60,
    preyDrive: 30,
    stamina: 55,
  },
  "Bernese Retriever": {
    size: "large",
    energyLevel: 55,
    exerciseNeeds: 55,
    heatTolerance: 25,
    coldTolerance: 75,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 65,
    preyDrive: 30,
    stamina: 60,
  },
  "Bichon Frise": {
    size: "small",
    energyLevel: 50,
    exerciseNeeds: 35,
    heatTolerance: 30,
    coldTolerance: 25,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 60,
    preyDrive: 15,
    stamina: 25,
  },
  "Biewer Terrier": {
    size: "tiny",
    energyLevel: 50,
    exerciseNeeds: 35,
    heatTolerance: 25,
    coldTolerance: 20,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 25,
    stamina: 25,
  },
  "Black Russian Terrier": {
    size: "giant",
    energyLevel: 60,
    exerciseNeeds: 60,
    heatTolerance: 25,
    coldTolerance: 80,
    dogFriendliness: 35,
    peopleFriendliness: 40,
    trainability: 60,
    preyDrive: 60,
    stamina: 60,
  },
  "Black and Tan Coonhound": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 50,
    coldTolerance: 40,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 35,
    preyDrive: 85,
    stamina: 70,
  },
  "Bloodhound": {
    size: "large",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 35,
    coldTolerance: 35,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 25,
    preyDrive: 80,
    stamina: 50,
  },
  "Blue Picardy Spaniel": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 60,
    stamina: 65,
  },
  "Bluetick Coonhound": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 50,
    coldTolerance: 40,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 35,
    preyDrive: 90,
    stamina: 75,
  },
  "Boerboel": {
    size: "giant",
    energyLevel: 55,
    exerciseNeeds: 55,
    heatTolerance: 60,
    coldTolerance: 25,
    dogFriendliness: 25,
    peopleFriendliness: 35,
    trainability: 50,
    preyDrive: 65,
    stamina: 55,
  },
  "Bohemian Shepherd": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 60,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 65,
    preyDrive: 50,
    stamina: 65,
  },
  "Bolognese": {
    size: "small",
    energyLevel: 35,
    exerciseNeeds: 30,
    heatTolerance: 30,
    coldTolerance: 25,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 10,
    stamina: 20,
  },
  "Borador": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 75,
    preyDrive: 45,
    stamina: 75,
  },
  "Border Collie": {
    size: "medium",
    energyLevel: 95,
    exerciseNeeds: 90,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 90,
    preyDrive: 65,
    stamina: 85,
  },
  "Border Collie Mix": {
    size: "medium",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 75,
    preyDrive: 55,
    stamina: 75,
  },
  "Border Terrier": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 70,
    peopleFriendliness: 70,
    trainability: 60,
    preyDrive: 65,
    stamina: 55,
  },
  "Borzoi": {
    size: "giant",
    energyLevel: 50,
    exerciseNeeds: 45,
    heatTolerance: 30,
    coldTolerance: 60,
    dogFriendliness: 35,
    peopleFriendliness: 30,
    trainability: 30,
    preyDrive: 85,
    stamina: 50,
  },
  "Boston Terrier": {
    size: "small",
    energyLevel: 55,
    exerciseNeeds: 40,
    heatTolerance: 20,
    coldTolerance: 15,
    dogFriendliness: 75,
    peopleFriendliness: 85,
    trainability: 60,
    preyDrive: 25,
    stamina: 30,
  },
  "Boston Terrier Mix": {
    size: "small",
    energyLevel: 55,
    exerciseNeeds: 40,
    heatTolerance: 25,
    coldTolerance: 20,
    dogFriendliness: 70,
    peopleFriendliness: 80,
    trainability: 55,
    preyDrive: 30,
    stamina: 35,
  },
  "Bouvier des Ardennes": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 35,
    coldTolerance: 65,
    dogFriendliness: 50,
    peopleFriendliness: 45,
    trainability: 55,
    preyDrive: 50,
    stamina: 65,
  },
  "Bouvier des Flandres": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 30,
    coldTolerance: 75,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 60,
    preyDrive: 55,
    stamina: 60,
  },
  "Boxer": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 25,
    coldTolerance: 25,
    dogFriendliness: 70,
    peopleFriendliness: 85,
    trainability: 60,
    preyDrive: 45,
    stamina: 65,
  },
  "Boxer Mix": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 30,
    coldTolerance: 30,
    dogFriendliness: 65,
    peopleFriendliness: 80,
    trainability: 55,
    preyDrive: 45,
    stamina: 60,
  },
  "Boykin Spaniel": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 50,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 60,
    preyDrive: 55,
    stamina: 65,
  },
  "Bracco Italiano": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 70,
    heatTolerance: 55,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 50,
    preyDrive: 65,
    stamina: 70,
  },
  "Braque Francais Pyrenean": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 55,
    coldTolerance: 40,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 65,
    stamina: 65,
  },
  "Braque Saint-Germain": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 55,
    coldTolerance: 40,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 60,
    stamina: 65,
  },
  "Braque du Bourbonnais": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 50,
    coldTolerance: 40,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 60,
    stamina: 60,
  },
  "Brazilian Terrier": {
    size: "small",
    energyLevel: 75,
    exerciseNeeds: 60,
    heatTolerance: 60,
    coldTolerance: 25,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 65,
    stamina: 45,
  },
  "Briard": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 65,
    heatTolerance: 35,
    coldTolerance: 70,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 55,
    preyDrive: 55,
    stamina: 65,
  },
  "Brittany": {
    size: "medium",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 50,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 70,
    preyDrive: 70,
    stamina: 75,
  },
  "Broholmer": {
    size: "giant",
    energyLevel: 45,
    exerciseNeeds: 45,
    heatTolerance: 30,
    coldTolerance: 60,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 35,
    stamina: 45,
  },
  "Brussels Griffon": {
    size: "tiny",
    energyLevel: 50,
    exerciseNeeds: 35,
    heatTolerance: 20,
    coldTolerance: 15,
    dogFriendliness: 50,
    peopleFriendliness: 65,
    trainability: 45,
    preyDrive: 25,
    stamina: 20,
  },
  "Bull Terrier": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 40,
    coldTolerance: 30,
    dogFriendliness: 40,
    peopleFriendliness: 60,
    trainability: 40,
    preyDrive: 55,
    stamina: 55,
  },
  "Bull Terrier Mix": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 40,
    coldTolerance: 30,
    dogFriendliness: 45,
    peopleFriendliness: 60,
    trainability: 45,
    preyDrive: 50,
    stamina: 50,
  },
  "Bullboxer": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 30,
    coldTolerance: 30,
    dogFriendliness: 50,
    peopleFriendliness: 65,
    trainability: 50,
    preyDrive: 50,
    stamina: 55,
  },
  "Bulldog": {
    size: "medium",
    energyLevel: 30,
    exerciseNeeds: 25,
    heatTolerance: 15,
    coldTolerance: 15,
    dogFriendliness: 55,
    peopleFriendliness: 75,
    trainability: 40,
    preyDrive: 20,
    stamina: 20,
  },
  "Bullhuahua": {
    size: "small",
    energyLevel: 40,
    exerciseNeeds: 30,
    heatTolerance: 20,
    coldTolerance: 15,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 35,
    preyDrive: 25,
    stamina: 25,
  },
  "Bullmastiff": {
    size: "giant",
    energyLevel: 40,
    exerciseNeeds: 35,
    heatTolerance: 25,
    coldTolerance: 30,
    dogFriendliness: 40,
    peopleFriendliness: 55,
    trainability: 45,
    preyDrive: 50,
    stamina: 35,
  },
  "Cairn Terrier": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 50,
    preyDrive: 70,
    stamina: 45,
  },
  "Calupoh": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 55,
    preyDrive: 65,
    stamina: 65,
  },
  "Canaan Dog": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 75,
    coldTolerance: 25,
    dogFriendliness: 30,
    peopleFriendliness: 30,
    trainability: 45,
    preyDrive: 65,
    stamina: 55,
  },
  "Canadian Eskimo Dog": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 70,
    heatTolerance: 10,
    coldTolerance: 95,
    dogFriendliness: 50,
    peopleFriendliness: 45,
    trainability: 35,
    preyDrive: 70,
    stamina: 80,
  },
  "Cane Corso": {
    size: "giant",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 45,
    coldTolerance: 35,
    dogFriendliness: 25,
    peopleFriendliness: 35,
    trainability: 55,
    preyDrive: 65,
    stamina: 55,
  },
  "Cardigan Welsh Corgi": {
    size: "small",
    energyLevel: 60,
    exerciseNeeds: 50,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 45,
    stamina: 45,
  },
  "Carolina Dog": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 60,
    coldTolerance: 35,
    dogFriendliness: 40,
    peopleFriendliness: 35,
    trainability: 40,
    preyDrive: 70,
    stamina: 55,
  },
  "Castro Laboreiro Dog": {
    size: "large",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 45,
    coldTolerance: 55,
    dogFriendliness: 35,
    peopleFriendliness: 30,
    trainability: 45,
    preyDrive: 65,
    stamina: 60,
  },
  "Catahoula Leopard Dog": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 60,
    coldTolerance: 35,
    dogFriendliness: 40,
    peopleFriendliness: 45,
    trainability: 45,
    preyDrive: 75,
    stamina: 80,
  },
  "Catalan Sheepdog": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 55,
    dogFriendliness: 55,
    peopleFriendliness: 50,
    trainability: 55,
    preyDrive: 45,
    stamina: 60,
  },
  "Caucasian Shepherd Dog": {
    size: "giant",
    energyLevel: 50,
    exerciseNeeds: 45,
    heatTolerance: 30,
    coldTolerance: 80,
    dogFriendliness: 20,
    peopleFriendliness: 20,
    trainability: 30,
    preyDrive: 70,
    stamina: 50,
  },
  "Cavalier King Charles Spaniel": {
    size: "small",
    energyLevel: 40,
    exerciseNeeds: 30,
    heatTolerance: 25,
    coldTolerance: 20,
    dogFriendliness: 80,
    peopleFriendliness: 90,
    trainability: 60,
    preyDrive: 15,
    stamina: 20,
  },
  "Cavapoo": {
    size: "small",
    energyLevel: 50,
    exerciseNeeds: 40,
    heatTolerance: 30,
    coldTolerance: 25,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 65,
    preyDrive: 15,
    stamina: 30,
  },
  "Central Asian Shepherd Dog": {
    size: "giant",
    energyLevel: 50,
    exerciseNeeds: 50,
    heatTolerance: 45,
    coldTolerance: 70,
    dogFriendliness: 20,
    peopleFriendliness: 25,
    trainability: 30,
    preyDrive: 70,
    stamina: 55,
  },
  "Cesky Terrier": {
    size: "small",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 35,
    coldTolerance: 40,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 50,
    stamina: 35,
  },
  "Cheagle": {
    size: "small",
    energyLevel: 60,
    exerciseNeeds: 45,
    heatTolerance: 30,
    coldTolerance: 25,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 35,
    preyDrive: 55,
    stamina: 30,
  },
  "Chesapeake Bay Retriever": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 45,
    coldTolerance: 70,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 60,
    preyDrive: 65,
    stamina: 80,
  },
  "Chihuahua": {
    size: "tiny",
    energyLevel: 50,
    exerciseNeeds: 30,
    heatTolerance: 30,
    coldTolerance: 10,
    dogFriendliness: 35,
    peopleFriendliness: 45,
    trainability: 35,
    preyDrive: 30,
    stamina: 15,
  },
  "Chihuahua Mix": {
    size: "tiny",
    energyLevel: 50,
    exerciseNeeds: 30,
    heatTolerance: 30,
    coldTolerance: 15,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 40,
    preyDrive: 30,
    stamina: 20,
  },
  "Chinese Crested": {
    size: "small",
    energyLevel: 45,
    exerciseNeeds: 35,
    heatTolerance: 55,
    coldTolerance: 5,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 50,
    preyDrive: 20,
    stamina: 20,
  },
  "Chinese Shar-Pei": {
    size: "medium",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 40,
    coldTolerance: 35,
    dogFriendliness: 25,
    peopleFriendliness: 35,
    trainability: 35,
    preyDrive: 50,
    stamina: 35,
  },
  "Chinook": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 25,
    coldTolerance: 80,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 60,
    preyDrive: 35,
    stamina: 70,
  },
  "Chiweenie": {
    size: "tiny",
    energyLevel: 55,
    exerciseNeeds: 35,
    heatTolerance: 30,
    coldTolerance: 10,
    dogFriendliness: 40,
    peopleFriendliness: 55,
    trainability: 30,
    preyDrive: 35,
    stamina: 20,
  },
  "Chorkie": {
    size: "tiny",
    energyLevel: 55,
    exerciseNeeds: 35,
    heatTolerance: 25,
    coldTolerance: 10,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 30,
    preyDrive: 30,
    stamina: 20,
  },
  "Chow Chow": {
    size: "large",
    energyLevel: 40,
    exerciseNeeds: 35,
    heatTolerance: 20,
    coldTolerance: 65,
    dogFriendliness: 20,
    peopleFriendliness: 25,
    trainability: 25,
    preyDrive: 55,
    stamina: 35,
  },
  "Cirneco dell’Etna": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 75,
    coldTolerance: 20,
    dogFriendliness: 45,
    peopleFriendliness: 50,
    trainability: 45,
    preyDrive: 80,
    stamina: 55,
  },
  "Clumber Spaniel": {
    size: "large",
    energyLevel: 35,
    exerciseNeeds: 35,
    heatTolerance: 25,
    coldTolerance: 40,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 45,
    preyDrive: 55,
    stamina: 30,
  },
  "Cockapoo": {
    size: "small",
    energyLevel: 60,
    exerciseNeeds: 50,
    heatTolerance: 35,
    coldTolerance: 30,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 70,
    preyDrive: 25,
    stamina: 40,
  },
  "Collie": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 35,
    coldTolerance: 55,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 75,
    preyDrive: 45,
    stamina: 65,
  },
  "Collie Mix": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 65,
    preyDrive: 45,
    stamina: 60,
  },
  "Corgi Shepherd": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 60,
    preyDrive: 55,
    stamina: 55,
  },
  "Coton de Tulear": {
    size: "small",
    energyLevel: 45,
    exerciseNeeds: 35,
    heatTolerance: 30,
    coldTolerance: 20,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 60,
    preyDrive: 10,
    stamina: 25,
  },
  "Croatian Sheepdog": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 45,
    peopleFriendliness: 40,
    trainability: 60,
    preyDrive: 55,
    stamina: 65,
  },
  "Curly-Coated Retriever": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 50,
    coldTolerance: 55,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 60,
    stamina: 70,
  },
  "Czechoslovakian Vlciak": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 35,
    coldTolerance: 75,
    dogFriendliness: 35,
    peopleFriendliness: 30,
    trainability: 50,
    preyDrive: 70,
    stamina: 80,
  },
  "Dachshund": {
    size: "small",
    energyLevel: 55,
    exerciseNeeds: 45,
    heatTolerance: 35,
    coldTolerance: 25,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 35,
    preyDrive: 75,
    stamina: 30,
  },
  "Dachshund Mix": {
    size: "small",
    energyLevel: 55,
    exerciseNeeds: 45,
    heatTolerance: 35,
    coldTolerance: 25,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 40,
    preyDrive: 65,
    stamina: 30,
  },
  "Dalmatian": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 50,
    preyDrive: 45,
    stamina: 80,
  },
  "Dandie Dinmont Terrier": {
    size: "small",
    energyLevel: 45,
    exerciseNeeds: 35,
    heatTolerance: 35,
    coldTolerance: 35,
    dogFriendliness: 50,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 60,
    stamina: 30,
  },
  "Daniff": {
    size: "giant",
    energyLevel: 40,
    exerciseNeeds: 35,
    heatTolerance: 20,
    coldTolerance: 30,
    dogFriendliness: 50,
    peopleFriendliness: 60,
    trainability: 40,
    preyDrive: 35,
    stamina: 35,
  },
  "Danish-Swedish Farmdog": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 40,
    coldTolerance: 40,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 60,
    preyDrive: 55,
    stamina: 50,
  },
  "Deutscher Wachtelhund": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 60,
    preyDrive: 75,
    stamina: 75,
  },
  "Doberman Pinscher": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 40,
    coldTolerance: 25,
    dogFriendliness: 40,
    peopleFriendliness: 55,
    trainability: 75,
    preyDrive: 60,
    stamina: 75,
  },
  "Doberman Shepherd Mix": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 40,
    coldTolerance: 35,
    dogFriendliness: 45,
    peopleFriendliness: 50,
    trainability: 70,
    preyDrive: 60,
    stamina: 75,
  },
  "Dogo Argentino": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 70,
    heatTolerance: 55,
    coldTolerance: 25,
    dogFriendliness: 30,
    peopleFriendliness: 40,
    trainability: 50,
    preyDrive: 80,
    stamina: 70,
  },
  "Dogue de Bordeaux": {
    size: "giant",
    energyLevel: 40,
    exerciseNeeds: 35,
    heatTolerance: 25,
    coldTolerance: 25,
    dogFriendliness: 35,
    peopleFriendliness: 50,
    trainability: 35,
    preyDrive: 55,
    stamina: 30,
  },
  "Doxiepoo": {
    size: "small",
    energyLevel: 55,
    exerciseNeeds: 45,
    heatTolerance: 35,
    coldTolerance: 25,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 45,
    stamina: 35,
  },
  "Drentsche Patrijshond": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 60,
    preyDrive: 65,
    stamina: 65,
  },
  "Drever": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 60,
    peopleFriendliness: 60,
    trainability: 40,
    preyDrive: 80,
    stamina: 50,
  },
  "Dutch Shepherd": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 75,
    preyDrive: 65,
    stamina: 80,
  },
  "English Cocker Spaniel": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 40,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 65,
    preyDrive: 55,
    stamina: 60,
  },
  "English Foxhound": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 50,
    coldTolerance: 40,
    dogFriendliness: 70,
    peopleFriendliness: 60,
    trainability: 35,
    preyDrive: 85,
    stamina: 80,
  },
  "English Setter": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 55,
    preyDrive: 65,
    stamina: 65,
  },
  "English Springer Spaniel": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 40,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 70,
    preyDrive: 60,
    stamina: 70,
  },
  "English Toy Spaniel": {
    size: "tiny",
    energyLevel: 30,
    exerciseNeeds: 20,
    heatTolerance: 20,
    coldTolerance: 15,
    dogFriendliness: 65,
    peopleFriendliness: 75,
    trainability: 50,
    preyDrive: 10,
    stamina: 15,
  },
  "Entlebucher Mountain Dog": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 30,
    coldTolerance: 65,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 60,
    preyDrive: 50,
    stamina: 70,
  },
  "Estrela Mountain Dog": {
    size: "giant",
    energyLevel: 50,
    exerciseNeeds: 50,
    heatTolerance: 40,
    coldTolerance: 60,
    dogFriendliness: 35,
    peopleFriendliness: 35,
    trainability: 40,
    preyDrive: 65,
    stamina: 55,
  },
  "Eurasier": {
    size: "medium",
    energyLevel: 50,
    exerciseNeeds: 50,
    heatTolerance: 30,
    coldTolerance: 70,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 55,
    preyDrive: 35,
    stamina: 50,
  },
  "Field Spaniel": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 40,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 60,
    preyDrive: 55,
    stamina: 55,
  },
  "Finnish Lapphund": {
    size: "medium",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 20,
    coldTolerance: 85,
    dogFriendliness: 65,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 40,
    stamina: 55,
  },
  "Finnish Spitz": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 30,
    coldTolerance: 80,
    dogFriendliness: 45,
    peopleFriendliness: 50,
    trainability: 45,
    preyDrive: 70,
    stamina: 60,
  },
  "Flat-Coated Retriever": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 70,
    preyDrive: 55,
    stamina: 70,
  },
  "French Bulldog": {
    size: "small",
    energyLevel: 35,
    exerciseNeeds: 25,
    heatTolerance: 15,
    coldTolerance: 10,
    dogFriendliness: 70,
    peopleFriendliness: 85,
    trainability: 45,
    preyDrive: 20,
    stamina: 15,
  },
  "French Spaniel": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 40,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 60,
    preyDrive: 55,
    stamina: 65,
  },
  "Galgo Español": {
    size: "large",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 70,
    coldTolerance: 20,
    dogFriendliness: 50,
    peopleFriendliness: 45,
    trainability: 40,
    preyDrive: 75,
    stamina: 55,
  },
  "Garafian Shepherd": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 55,
    coldTolerance: 50,
    dogFriendliness: 45,
    peopleFriendliness: 40,
    trainability: 50,
    preyDrive: 60,
    stamina: 60,
  },
  "German Longhaired Pointer": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 65,
    preyDrive: 70,
    stamina: 75,
  },
  "German Pinscher": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 35,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 60,
    preyDrive: 55,
    stamina: 55,
  },
  "German Shepherd Dog": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 80,
    preyDrive: 65,
    stamina: 80,
  },
  "German Shepherd Mix": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 70,
    preyDrive: 55,
    stamina: 70,
  },
  "German Shorthaired Pointer": {
    size: "large",
    energyLevel: 90,
    exerciseNeeds: 85,
    heatTolerance: 50,
    coldTolerance: 45,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 70,
    preyDrive: 75,
    stamina: 85,
  },
  "German Spitz": {
    size: "small",
    energyLevel: 60,
    exerciseNeeds: 50,
    heatTolerance: 30,
    coldTolerance: 65,
    dogFriendliness: 45,
    peopleFriendliness: 50,
    trainability: 50,
    preyDrive: 35,
    stamina: 45,
  },
  "German Wirehaired Pointer": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 50,
    coldTolerance: 55,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 65,
    preyDrive: 75,
    stamina: 80,
  },
  "Giant Schnauzer": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 35,
    peopleFriendliness: 40,
    trainability: 60,
    preyDrive: 60,
    stamina: 70,
  },
  "Glen of Imaal Terrier": {
    size: "small",
    energyLevel: 50,
    exerciseNeeds: 45,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 50,
    preyDrive: 60,
    stamina: 40,
  },
  "Goldador": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 70,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 80,
    preyDrive: 45,
    stamina: 70,
  },
  "Golden Retriever": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 85,
    peopleFriendliness: 90,
    trainability: 80,
    preyDrive: 45,
    stamina: 70,
  },
  "Golden Retriever Mix": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 70,
    preyDrive: 45,
    stamina: 65,
  },
  "Goldendoodle": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 75,
    preyDrive: 30,
    stamina: 60,
  },
  "Gordon Setter": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 75,
    heatTolerance: 35,
    coldTolerance: 55,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 65,
    stamina: 70,
  },
  "Gos d’Atura Català": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 50,
    coldTolerance: 45,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 55,
    preyDrive: 45,
    stamina: 55,
  },
  "Grand Basset Griffon Vendéen": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 70,
    trainability: 40,
    preyDrive: 75,
    stamina: 65,
  },
  "Great Dane": {
    size: "giant",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 25,
    coldTolerance: 25,
    dogFriendliness: 65,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 35,
    stamina: 35,
  },
  "Great Dane Mix": {
    size: "giant",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 30,
    coldTolerance: 30,
    dogFriendliness: 65,
    peopleFriendliness: 75,
    trainability: 50,
    preyDrive: 35,
    stamina: 35,
  },
  "Great Pyrenees": {
    size: "giant",
    energyLevel: 40,
    exerciseNeeds: 40,
    heatTolerance: 20,
    coldTolerance: 90,
    dogFriendliness: 45,
    peopleFriendliness: 40,
    trainability: 30,
    preyDrive: 55,
    stamina: 45,
  },
  "Greater Swiss Mountain Dog": {
    size: "giant",
    energyLevel: 50,
    exerciseNeeds: 50,
    heatTolerance: 20,
    coldTolerance: 80,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 35,
    stamina: 50,
  },
  "Greyhound": {
    size: "large",
    energyLevel: 40,
    exerciseNeeds: 35,
    heatTolerance: 40,
    coldTolerance: 20,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 45,
    preyDrive: 80,
    stamina: 40,
  },
  "Greyhound Mix": {
    size: "large",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 45,
    coldTolerance: 25,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 70,
    stamina: 40,
  },
  "Hamiltonstovare": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 80,
    stamina: 70,
  },
  "Hanoverian Scenthound": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 40,
    peopleFriendliness: 40,
    trainability: 50,
    preyDrive: 85,
    stamina: 65,
  },
  "Harrier": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 50,
    coldTolerance: 45,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 40,
    preyDrive: 85,
    stamina: 75,
  },
  "Havanese": {
    size: "small",
    energyLevel: 50,
    exerciseNeeds: 40,
    heatTolerance: 35,
    coldTolerance: 25,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 65,
    preyDrive: 15,
    stamina: 30,
  },
  "Havapoo": {
    size: "small",
    energyLevel: 50,
    exerciseNeeds: 40,
    heatTolerance: 35,
    coldTolerance: 25,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 65,
    preyDrive: 15,
    stamina: 30,
  },
  "Heeler Mix": {
    size: "medium",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 60,
    coldTolerance: 45,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 60,
    preyDrive: 60,
    stamina: 80,
  },
  "Hokkaido": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 25,
    coldTolerance: 85,
    dogFriendliness: 35,
    peopleFriendliness: 40,
    trainability: 50,
    preyDrive: 65,
    stamina: 65,
  },
  "Hovawart": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 35,
    coldTolerance: 65,
    dogFriendliness: 40,
    peopleFriendliness: 45,
    trainability: 60,
    preyDrive: 55,
    stamina: 65,
  },
  "Husky Mix": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 75,
    heatTolerance: 25,
    coldTolerance: 70,
    dogFriendliness: 60,
    peopleFriendliness: 55,
    trainability: 40,
    preyDrive: 60,
    stamina: 75,
  },
  "Husky Shepherd": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 30,
    coldTolerance: 65,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 55,
    preyDrive: 65,
    stamina: 80,
  },
  "Huskydoodle": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 30,
    coldTolerance: 60,
    dogFriendliness: 65,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 50,
    stamina: 70,
  },
  "Ibizan Hound": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 75,
    coldTolerance: 15,
    dogFriendliness: 50,
    peopleFriendliness: 55,
    trainability: 40,
    preyDrive: 85,
    stamina: 65,
  },
  "Icelandic Sheepdog": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 20,
    coldTolerance: 85,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 45,
    stamina: 55,
  },
  "Irish Doodle": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 70,
    preyDrive: 40,
    stamina: 65,
  },
  "Irish Red and White Setter": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 65,
    stamina: 70,
  },
  "Irish Setter": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 55,
    preyDrive: 65,
    stamina: 70,
  },
  "Irish Terrier": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 45,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 60,
    stamina: 65,
  },
  "Irish Water Spaniel": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 55,
    stamina: 75,
  },
  "Irish Wolfhound": {
    size: "giant",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 25,
    coldTolerance: 50,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 40,
    preyDrive: 65,
    stamina: 40,
  },
  "Italian Greyhound": {
    size: "tiny",
    energyLevel: 45,
    exerciseNeeds: 30,
    heatTolerance: 45,
    coldTolerance: 10,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 35,
    preyDrive: 55,
    stamina: 20,
  },
  "Jack Russell Terrier": {
    size: "small",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 70,
    trainability: 45,
    preyDrive: 80,
    stamina: 65,
  },
  "Jack Russell Terrier Mix": {
    size: "small",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 60,
    peopleFriendliness: 70,
    trainability: 45,
    preyDrive: 70,
    stamina: 60,
  },
  "Japanese Akitainu": {
    size: "large",
    energyLevel: 50,
    exerciseNeeds: 45,
    heatTolerance: 25,
    coldTolerance: 80,
    dogFriendliness: 25,
    peopleFriendliness: 30,
    trainability: 35,
    preyDrive: 65,
    stamina: 50,
  },
  "Japanese Chin": {
    size: "tiny",
    energyLevel: 25,
    exerciseNeeds: 20,
    heatTolerance: 25,
    coldTolerance: 10,
    dogFriendliness: 65,
    peopleFriendliness: 75,
    trainability: 45,
    preyDrive: 10,
    stamina: 10,
  },
  "Japanese Spitz": {
    size: "small",
    energyLevel: 55,
    exerciseNeeds: 45,
    heatTolerance: 25,
    coldTolerance: 70,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 25,
    stamina: 40,
  },
  "Japanese Terrier": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 45,
    coldTolerance: 25,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 50,
    preyDrive: 55,
    stamina: 45,
  },
  "Kai Ken": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 30,
    coldTolerance: 75,
    dogFriendliness: 35,
    peopleFriendliness: 35,
    trainability: 50,
    preyDrive: 70,
    stamina: 65,
  },
  "Karelian Bear Dog": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 25,
    coldTolerance: 85,
    dogFriendliness: 25,
    peopleFriendliness: 25,
    trainability: 40,
    preyDrive: 90,
    stamina: 75,
  },
  "Keeshond": {
    size: "medium",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 25,
    coldTolerance: 75,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 60,
    preyDrive: 30,
    stamina: 50,
  },
  "Kelpie Mix": {
    size: "medium",
    energyLevel: 90,
    exerciseNeeds: 85,
    heatTolerance: 60,
    coldTolerance: 40,
    dogFriendliness: 50,
    peopleFriendliness: 45,
    trainability: 60,
    preyDrive: 60,
    stamina: 85,
  },
  "Kerry Blue Terrier": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 40,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 55,
    stamina: 60,
  },
  "Kishu Ken": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 30,
    coldTolerance: 75,
    dogFriendliness: 30,
    peopleFriendliness: 30,
    trainability: 45,
    preyDrive: 70,
    stamina: 60,
  },
  "Komondor": {
    size: "giant",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 25,
    coldTolerance: 75,
    dogFriendliness: 25,
    peopleFriendliness: 25,
    trainability: 30,
    preyDrive: 60,
    stamina: 40,
  },
  "Korean Jindo Dog": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 25,
    peopleFriendliness: 30,
    trainability: 45,
    preyDrive: 75,
    stamina: 65,
  },
  "Kromfohrlander": {
    size: "medium",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 60,
    preyDrive: 40,
    stamina: 50,
  },
  "Kuvasz": {
    size: "giant",
    energyLevel: 55,
    exerciseNeeds: 55,
    heatTolerance: 30,
    coldTolerance: 70,
    dogFriendliness: 25,
    peopleFriendliness: 30,
    trainability: 35,
    preyDrive: 65,
    stamina: 55,
  },
  "Labernese": {
    size: "large",
    energyLevel: 60,
    exerciseNeeds: 60,
    heatTolerance: 25,
    coldTolerance: 70,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 70,
    preyDrive: 35,
    stamina: 60,
  },
  "Labrabull": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 35,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 50,
    stamina: 60,
  },
  "Labrador Retriever": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 90,
    peopleFriendliness: 90,
    trainability: 80,
    preyDrive: 45,
    stamina: 70,
  },
  "Labrador Retriever Mix": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 85,
    peopleFriendliness: 85,
    trainability: 70,
    preyDrive: 45,
    stamina: 65,
  },
  "Labrakita": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 35,
    coldTolerance: 60,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 55,
    stamina: 60,
  },
  "Lagotto Romagnolo": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 70,
    peopleFriendliness: 70,
    trainability: 70,
    preyDrive: 50,
    stamina: 60,
  },
  "Lakeland Terrier": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 50,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 65,
    stamina: 50,
  },
  "Lancashire Heeler": {
    size: "tiny",
    energyLevel: 70,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 40,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 55,
    stamina: 40,
  },
  "Lapponian Herder": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 20,
    coldTolerance: 85,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 45,
    stamina: 65,
  },
  "Large Munsterlander": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 70,
    peopleFriendliness: 70,
    trainability: 65,
    preyDrive: 65,
    stamina: 75,
  },
  "Leonberger": {
    size: "giant",
    energyLevel: 55,
    exerciseNeeds: 55,
    heatTolerance: 20,
    coldTolerance: 75,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 55,
    preyDrive: 30,
    stamina: 55,
  },
  "Leonese Shepherd Dog": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 35,
    coldTolerance: 55,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 55,
    preyDrive: 55,
    stamina: 60,
  },
  "Lhasa Apso": {
    size: "small",
    energyLevel: 40,
    exerciseNeeds: 35,
    heatTolerance: 30,
    coldTolerance: 35,
    dogFriendliness: 35,
    peopleFriendliness: 40,
    trainability: 40,
    preyDrive: 35,
    stamina: 25,
  },
  "Löwchen": {
    size: "small",
    energyLevel: 45,
    exerciseNeeds: 35,
    heatTolerance: 30,
    coldTolerance: 25,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 20,
    stamina: 25,
  },
  "Malamute Shepherd Mix": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 70,
    heatTolerance: 20,
    coldTolerance: 80,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 50,
    preyDrive: 55,
    stamina: 75,
  },
  "Malshi": {
    size: "tiny",
    energyLevel: 40,
    exerciseNeeds: 30,
    heatTolerance: 25,
    coldTolerance: 15,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 15,
    stamina: 20,
  },
  "Maltese": {
    size: "tiny",
    energyLevel: 45,
    exerciseNeeds: 30,
    heatTolerance: 25,
    coldTolerance: 10,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 15,
    stamina: 20,
  },
  "Maltese Mix": {
    size: "tiny",
    energyLevel: 45,
    exerciseNeeds: 30,
    heatTolerance: 25,
    coldTolerance: 12,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 18,
    stamina: 20,
  },
  "Maltipoo": {
    size: "small",
    energyLevel: 50,
    exerciseNeeds: 40,
    heatTolerance: 30,
    coldTolerance: 20,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 65,
    preyDrive: 12,
    stamina: 30,
  },
  "Manchester Terrier": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 25,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 65,
    stamina: 50,
  },
  "Mastiff": {
    size: "giant",
    energyLevel: 30,
    exerciseNeeds: 25,
    heatTolerance: 20,
    coldTolerance: 25,
    dogFriendliness: 50,
    peopleFriendliness: 65,
    trainability: 40,
    preyDrive: 40,
    stamina: 25,
  },
  "Mastiff Mix": {
    size: "giant",
    energyLevel: 35,
    exerciseNeeds: 30,
    heatTolerance: 25,
    coldTolerance: 30,
    dogFriendliness: 50,
    peopleFriendliness: 60,
    trainability: 40,
    preyDrive: 45,
    stamina: 30,
  },
  "Miniature American Shepherd": {
    size: "small",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 35,
    coldTolerance: 50,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 70,
    preyDrive: 55,
    stamina: 65,
  },
  "Miniature Bull Terrier": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 40,
    coldTolerance: 30,
    dogFriendliness: 40,
    peopleFriendliness: 60,
    trainability: 40,
    preyDrive: 55,
    stamina: 50,
  },
  "Miniature Pinscher": {
    size: "tiny",
    energyLevel: 75,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 15,
    dogFriendliness: 40,
    peopleFriendliness: 55,
    trainability: 45,
    preyDrive: 55,
    stamina: 35,
  },
  "Miniature Schnauzer": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 40,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 60,
    preyDrive: 50,
    stamina: 45,
  },
  "Morkie": {
    size: "tiny",
    energyLevel: 50,
    exerciseNeeds: 35,
    heatTolerance: 25,
    coldTolerance: 12,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 45,
    preyDrive: 15,
    stamina: 20,
  },
  "Mountain Cur": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 55,
    coldTolerance: 40,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 50,
    preyDrive: 80,
    stamina: 75,
  },
  "Mountain Cur Mix": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 55,
    coldTolerance: 40,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 50,
    preyDrive: 75,
    stamina: 70,
  },
  "Mudi": {
    size: "medium",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 70,
    preyDrive: 60,
    stamina: 75,
  },
  "Navarra Pointer": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 60,
    coldTolerance: 35,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 65,
    stamina: 65,
  },
  "Neapolitan Mastiff": {
    size: "giant",
    energyLevel: 30,
    exerciseNeeds: 25,
    heatTolerance: 20,
    coldTolerance: 20,
    dogFriendliness: 35,
    peopleFriendliness: 45,
    trainability: 35,
    preyDrive: 45,
    stamina: 20,
  },
  "Nederlandse Kooikerhondje": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 65,
    preyDrive: 55,
    stamina: 50,
  },
  "Newfoundland": {
    size: "giant",
    energyLevel: 45,
    exerciseNeeds: 45,
    heatTolerance: 15,
    coldTolerance: 90,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 55,
    preyDrive: 30,
    stamina: 45,
  },
  "Newfypoo": {
    size: "giant",
    energyLevel: 50,
    exerciseNeeds: 50,
    heatTolerance: 20,
    coldTolerance: 75,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 65,
    preyDrive: 25,
    stamina: 50,
  },
  "Norfolk Terrier": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 60,
    preyDrive: 60,
    stamina: 45,
  },
  "Norwegian Buhund": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 25,
    coldTolerance: 80,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 60,
    preyDrive: 50,
    stamina: 65,
  },
  "Norwegian Elkhound": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 25,
    coldTolerance: 85,
    dogFriendliness: 45,
    peopleFriendliness: 50,
    trainability: 50,
    preyDrive: 70,
    stamina: 70,
  },
  "Norwegian Lundehund": {
    size: "small",
    energyLevel: 60,
    exerciseNeeds: 50,
    heatTolerance: 25,
    coldTolerance: 75,
    dogFriendliness: 50,
    peopleFriendliness: 55,
    trainability: 40,
    preyDrive: 55,
    stamina: 50,
  },
  "Norwich Terrier": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 60,
    preyDrive: 60,
    stamina: 45,
  },
  "Nova Scotia Duck Tolling Retriever": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 40,
    coldTolerance: 60,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 75,
    preyDrive: 65,
    stamina: 75,
  },
  "Old English Sheepdog": {
    size: "large",
    energyLevel: 55,
    exerciseNeeds: 55,
    heatTolerance: 25,
    coldTolerance: 70,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 30,
    stamina: 55,
  },
  "Otterhound": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 35,
    coldTolerance: 55,
    dogFriendliness: 70,
    peopleFriendliness: 65,
    trainability: 40,
    preyDrive: 75,
    stamina: 65,
  },
  "Pachón Navarro": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 55,
    coldTolerance: 40,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 65,
    stamina: 60,
  },
  "Papillon": {
    size: "tiny",
    energyLevel: 65,
    exerciseNeeds: 45,
    heatTolerance: 35,
    coldTolerance: 15,
    dogFriendliness: 55,
    peopleFriendliness: 70,
    trainability: 70,
    preyDrive: 35,
    stamina: 30,
  },
  "Parson Russell Terrier": {
    size: "small",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 50,
    preyDrive: 80,
    stamina: 65,
  },
  "Pekingese": {
    size: "small",
    energyLevel: 25,
    exerciseNeeds: 20,
    heatTolerance: 20,
    coldTolerance: 25,
    dogFriendliness: 35,
    peopleFriendliness: 45,
    trainability: 30,
    preyDrive: 20,
    stamina: 10,
  },
  "Pembroke Welsh Corgi": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 70,
    peopleFriendliness: 80,
    trainability: 60,
    preyDrive: 45,
    stamina: 50,
  },
  "Perro de Agua Español": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 55,
    coldTolerance: 45,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 60,
    preyDrive: 45,
    stamina: 65,
  },
  "Peruvian Inca Orchid": {
    size: "medium",
    energyLevel: 55,
    exerciseNeeds: 45,
    heatTolerance: 70,
    coldTolerance: 5,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 50,
    stamina: 45,
  },
  "Petit Basset Griffon Vendéen": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 70,
    trainability: 40,
    preyDrive: 80,
    stamina: 60,
  },
  "Pharaoh Hound": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 80,
    coldTolerance: 10,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 45,
    preyDrive: 80,
    stamina: 60,
  },
  "Pit Bull Mix": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 30,
    dogFriendliness: 55,
    peopleFriendliness: 70,
    trainability: 50,
    preyDrive: 55,
    stamina: 65,
  },
  "Pitador": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 45,
    coldTolerance: 40,
    dogFriendliness: 70,
    peopleFriendliness: 80,
    trainability: 65,
    preyDrive: 45,
    stamina: 70,
  },
  "Pitsky": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 30,
    coldTolerance: 50,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 45,
    preyDrive: 60,
    stamina: 70,
  },
  "Plott Hound": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 55,
    coldTolerance: 40,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 45,
    preyDrive: 90,
    stamina: 75,
  },
  "Podenco Andaluz": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 75,
    coldTolerance: 20,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 40,
    preyDrive: 85,
    stamina: 70,
  },
  "Podenco Canario": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 75,
    coldTolerance: 20,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 35,
    preyDrive: 85,
    stamina: 65,
  },
  "Pointer": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 50,
    coldTolerance: 40,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 65,
    preyDrive: 80,
    stamina: 80,
  },
  "Polish Lowland Sheepdog": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 30,
    coldTolerance: 65,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 40,
    stamina: 55,
  },
  "Pomapoo": {
    size: "tiny",
    energyLevel: 55,
    exerciseNeeds: 40,
    heatTolerance: 30,
    coldTolerance: 20,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 15,
    stamina: 25,
  },
  "Pomchi": {
    size: "tiny",
    energyLevel: 60,
    exerciseNeeds: 40,
    heatTolerance: 25,
    coldTolerance: 18,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 35,
    preyDrive: 30,
    stamina: 20,
  },
  "Pomeranian": {
    size: "tiny",
    energyLevel: 60,
    exerciseNeeds: 40,
    heatTolerance: 25,
    coldTolerance: 20,
    dogFriendliness: 50,
    peopleFriendliness: 60,
    trainability: 45,
    preyDrive: 20,
    stamina: 25,
  },
  "Pomeranian Mix": {
    size: "tiny",
    energyLevel: 55,
    exerciseNeeds: 38,
    heatTolerance: 28,
    coldTolerance: 22,
    dogFriendliness: 55,
    peopleFriendliness: 62,
    trainability: 48,
    preyDrive: 22,
    stamina: 25,
  },
  "Pomsky": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 25,
    coldTolerance: 65,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 40,
    preyDrive: 50,
    stamina: 50,
  },
  "Pont-Audemer Spaniel": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 60,
    stamina: 65,
  },
  "Porcelaine": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 45,
    coldTolerance: 35,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 45,
    preyDrive: 80,
    stamina: 65,
  },
  "Portuguese Podengo": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 70,
    coldTolerance: 25,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 75,
    stamina: 55,
  },
  "Portuguese Podengo Pequeno": {
    size: "tiny",
    energyLevel: 65,
    exerciseNeeds: 50,
    heatTolerance: 70,
    coldTolerance: 20,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 70,
    stamina: 45,
  },
  "Portuguese Pointer": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 60,
    coldTolerance: 35,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 65,
    stamina: 65,
  },
  "Portuguese Sheepdog": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 55,
    coldTolerance: 45,
    dogFriendliness: 50,
    peopleFriendliness: 45,
    trainability: 55,
    preyDrive: 55,
    stamina: 60,
  },
  "Portuguese Water Dog": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 45,
    coldTolerance: 60,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 75,
    preyDrive: 45,
    stamina: 70,
  },
  "Presa Canario": {
    size: "large",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 55,
    coldTolerance: 30,
    dogFriendliness: 25,
    peopleFriendliness: 30,
    trainability: 45,
    preyDrive: 65,
    stamina: 55,
  },
  "Pudelpointer": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 55,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 65,
    preyDrive: 70,
    stamina: 75,
  },
  "Pug": {
    size: "small",
    energyLevel: 30,
    exerciseNeeds: 20,
    heatTolerance: 10,
    coldTolerance: 10,
    dogFriendliness: 70,
    peopleFriendliness: 85,
    trainability: 40,
    preyDrive: 15,
    stamina: 15,
  },
  "Puli": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 30,
    coldTolerance: 70,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 50,
    stamina: 65,
  },
  "Pumi": {
    size: "small",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 35,
    coldTolerance: 55,
    dogFriendliness: 50,
    peopleFriendliness: 55,
    trainability: 65,
    preyDrive: 55,
    stamina: 60,
  },
  "Pyredoodle": {
    size: "large",
    energyLevel: 50,
    exerciseNeeds: 50,
    heatTolerance: 25,
    coldTolerance: 75,
    dogFriendliness: 60,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 30,
    stamina: 50,
  },
  "Pyrenean Mastiff": {
    size: "giant",
    energyLevel: 40,
    exerciseNeeds: 40,
    heatTolerance: 25,
    coldTolerance: 80,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 35,
    preyDrive: 55,
    stamina: 45,
  },
  "Pyrenean Shepherd": {
    size: "small",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 30,
    coldTolerance: 70,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 55,
    preyDrive: 55,
    stamina: 70,
  },
  "Rafeiro do Alentejo": {
    size: "large",
    energyLevel: 50,
    exerciseNeeds: 45,
    heatTolerance: 60,
    coldTolerance: 40,
    dogFriendliness: 30,
    peopleFriendliness: 30,
    trainability: 40,
    preyDrive: 65,
    stamina: 50,
  },
  "Rat Terrier": {
    size: "small",
    energyLevel: 75,
    exerciseNeeds: 65,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 60,
    peopleFriendliness: 70,
    trainability: 60,
    preyDrive: 75,
    stamina: 55,
  },
  "Ratonero Bodeguero Andaluz": {
    size: "small",
    energyLevel: 75,
    exerciseNeeds: 65,
    heatTolerance: 65,
    coldTolerance: 25,
    dogFriendliness: 60,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 75,
    stamina: 55,
  },
  "Redbone Coonhound": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 50,
    coldTolerance: 40,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 40,
    preyDrive: 90,
    stamina: 75,
  },
  "Retriever Mix": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 70,
    preyDrive: 40,
    stamina: 60,
  },
  "Rhodesian Ridgeback": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 75,
    coldTolerance: 20,
    dogFriendliness: 40,
    peopleFriendliness: 50,
    trainability: 50,
    preyDrive: 75,
    stamina: 75,
  },
  "Romanian Carpathian Shepherd": {
    size: "large",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 30,
    coldTolerance: 75,
    dogFriendliness: 30,
    peopleFriendliness: 30,
    trainability: 40,
    preyDrive: 70,
    stamina: 60,
  },
  "Romanian Mioritic Shepherd Dog": {
    size: "giant",
    energyLevel: 50,
    exerciseNeeds: 50,
    heatTolerance: 30,
    coldTolerance: 80,
    dogFriendliness: 35,
    peopleFriendliness: 35,
    trainability: 35,
    preyDrive: 65,
    stamina: 55,
  },
  "Rottweiler": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 35,
    coldTolerance: 35,
    dogFriendliness: 30,
    peopleFriendliness: 45,
    trainability: 65,
    preyDrive: 55,
    stamina: 60,
  },
  "Rottweiler Shepherd Mix": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 70,
    heatTolerance: 35,
    coldTolerance: 40,
    dogFriendliness: 35,
    peopleFriendliness: 45,
    trainability: 65,
    preyDrive: 60,
    stamina: 70,
  },
  "Russell Terrier": {
    size: "small",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 45,
    preyDrive: 80,
    stamina: 65,
  },
  "Russian Toy": {
    size: "tiny",
    energyLevel: 55,
    exerciseNeeds: 35,
    heatTolerance: 30,
    coldTolerance: 10,
    dogFriendliness: 50,
    peopleFriendliness: 60,
    trainability: 45,
    preyDrive: 40,
    stamina: 20,
  },
  "Russian Tsvetnaya Bolonka": {
    size: "small",
    energyLevel: 40,
    exerciseNeeds: 30,
    heatTolerance: 25,
    coldTolerance: 40,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 10,
    stamina: 25,
  },
  "Saint Bernard": {
    size: "giant",
    energyLevel: 35,
    exerciseNeeds: 30,
    heatTolerance: 10,
    coldTolerance: 90,
    dogFriendliness: 65,
    peopleFriendliness: 75,
    trainability: 45,
    preyDrive: 30,
    stamina: 30,
  },
  "Saint Bernard Mix": {
    size: "giant",
    energyLevel: 40,
    exerciseNeeds: 35,
    heatTolerance: 15,
    coldTolerance: 85,
    dogFriendliness: 65,
    peopleFriendliness: 75,
    trainability: 45,
    preyDrive: 35,
    stamina: 35,
  },
  "Saint Pyrenees": {
    size: "giant",
    energyLevel: 35,
    exerciseNeeds: 35,
    heatTolerance: 15,
    coldTolerance: 90,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 35,
    preyDrive: 40,
    stamina: 40,
  },
  "Saluki": {
    size: "large",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 80,
    coldTolerance: 15,
    dogFriendliness: 40,
    peopleFriendliness: 40,
    trainability: 35,
    preyDrive: 85,
    stamina: 65,
  },
  "Samoyed": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 15,
    coldTolerance: 95,
    dogFriendliness: 80,
    peopleFriendliness: 85,
    trainability: 55,
    preyDrive: 35,
    stamina: 60,
  },
  "Samoyed Mix": {
    size: "medium",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 20,
    coldTolerance: 85,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 55,
    preyDrive: 35,
    stamina: 55,
  },
  "Schapendoes": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 30,
    coldTolerance: 65,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 35,
    stamina: 55,
  },
  "Schipperke": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 35,
    coldTolerance: 55,
    dogFriendliness: 45,
    peopleFriendliness: 50,
    trainability: 55,
    preyDrive: 45,
    stamina: 50,
  },
  "Schnoodle": {
    size: "medium",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 40,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 65,
    preyDrive: 30,
    stamina: 50,
  },
  "Scottish Deerhound": {
    size: "giant",
    energyLevel: 45,
    exerciseNeeds: 45,
    heatTolerance: 25,
    coldTolerance: 55,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 35,
    preyDrive: 75,
    stamina: 45,
  },
  "Scottish Terrier": {
    size: "small",
    energyLevel: 55,
    exerciseNeeds: 45,
    heatTolerance: 35,
    coldTolerance: 45,
    dogFriendliness: 35,
    peopleFriendliness: 40,
    trainability: 45,
    preyDrive: 60,
    stamina: 40,
  },
  "Sealyham Terrier": {
    size: "small",
    energyLevel: 50,
    exerciseNeeds: 45,
    heatTolerance: 35,
    coldTolerance: 45,
    dogFriendliness: 50,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 60,
    stamina: 40,
  },
  "Segugio Italiano": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 55,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 45,
    preyDrive: 85,
    stamina: 70,
  },
  "Sheepadoodle": {
    size: "large",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 30,
    coldTolerance: 60,
    dogFriendliness: 75,
    peopleFriendliness: 80,
    trainability: 70,
    preyDrive: 25,
    stamina: 55,
  },
  "Shepherd Mix": {
    size: "large",
    energyLevel: 70,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 50,
    dogFriendliness: 50,
    peopleFriendliness: 55,
    trainability: 60,
    preyDrive: 55,
    stamina: 65,
  },
  "Sheprador": {
    size: "large",
    energyLevel: 75,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 80,
    preyDrive: 50,
    stamina: 75,
  },
  "Shepsky": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 30,
    coldTolerance: 60,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 55,
    preyDrive: 65,
    stamina: 80,
  },
  "Shetland Sheepdog": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 30,
    coldTolerance: 55,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 75,
    preyDrive: 50,
    stamina: 55,
  },
  "Shiba Inu": {
    size: "medium",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 35,
    coldTolerance: 65,
    dogFriendliness: 30,
    peopleFriendliness: 35,
    trainability: 35,
    preyDrive: 60,
    stamina: 55,
  },
  "Shih Tzu": {
    size: "small",
    energyLevel: 35,
    exerciseNeeds: 25,
    heatTolerance: 20,
    coldTolerance: 15,
    dogFriendliness: 70,
    peopleFriendliness: 80,
    trainability: 40,
    preyDrive: 10,
    stamina: 15,
  },
  "Shih Tzu Mix": {
    size: "small",
    energyLevel: 38,
    exerciseNeeds: 28,
    heatTolerance: 22,
    coldTolerance: 18,
    dogFriendliness: 70,
    peopleFriendliness: 78,
    trainability: 42,
    preyDrive: 15,
    stamina: 18,
  },
  "Shikoku Ken": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 30,
    coldTolerance: 75,
    dogFriendliness: 30,
    peopleFriendliness: 35,
    trainability: 50,
    preyDrive: 70,
    stamina: 65,
  },
  "Shorkie": {
    size: "tiny",
    energyLevel: 50,
    exerciseNeeds: 35,
    heatTolerance: 22,
    coldTolerance: 12,
    dogFriendliness: 60,
    peopleFriendliness: 70,
    trainability: 40,
    preyDrive: 18,
    stamina: 20,
  },
  "Siberian Husky": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 80,
    heatTolerance: 15,
    coldTolerance: 95,
    dogFriendliness: 65,
    peopleFriendliness: 55,
    trainability: 35,
    preyDrive: 65,
    stamina: 85,
  },
  "Silky Terrier": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 50,
    heatTolerance: 40,
    coldTolerance: 25,
    dogFriendliness: 45,
    peopleFriendliness: 55,
    trainability: 55,
    preyDrive: 50,
    stamina: 40,
  },
  "Skye Terrier": {
    size: "medium",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 35,
    coldTolerance: 50,
    dogFriendliness: 30,
    peopleFriendliness: 35,
    trainability: 40,
    preyDrive: 55,
    stamina: 35,
  },
  "Sloughi": {
    size: "large",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 80,
    coldTolerance: 15,
    dogFriendliness: 35,
    peopleFriendliness: 30,
    trainability: 35,
    preyDrive: 80,
    stamina: 60,
  },
  "Slovakian Wirehaired Pointer": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 55,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 60,
    preyDrive: 75,
    stamina: 75,
  },
  "Slovensky Cuvac": {
    size: "large",
    energyLevel: 50,
    exerciseNeeds: 50,
    heatTolerance: 25,
    coldTolerance: 80,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 40,
    preyDrive: 55,
    stamina: 55,
  },
  "Slovensky Kopov": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 45,
    peopleFriendliness: 45,
    trainability: 50,
    preyDrive: 90,
    stamina: 70,
  },
  "Small Munsterlander": {
    size: "medium",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 70,
    peopleFriendliness: 70,
    trainability: 65,
    preyDrive: 70,
    stamina: 70,
  },
  "Smooth Fox Terrier": {
    size: "small",
    energyLevel: 75,
    exerciseNeeds: 65,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 45,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 75,
    stamina: 55,
  },
  "Soft Coated Wheaten Terrier": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 65,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 40,
    stamina: 55,
  },
  "Spanish Mastiff": {
    size: "giant",
    energyLevel: 40,
    exerciseNeeds: 35,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 30,
    peopleFriendliness: 30,
    trainability: 35,
    preyDrive: 60,
    stamina: 40,
  },
  "Spanish Water Dog": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 55,
    coldTolerance: 45,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 60,
    preyDrive: 50,
    stamina: 65,
  },
  "Spinone Italiano": {
    size: "large",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 55,
    preyDrive: 65,
    stamina: 60,
  },
  "Stabyhoun": {
    size: "medium",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 70,
    peopleFriendliness: 70,
    trainability: 60,
    preyDrive: 50,
    stamina: 55,
  },
  "Staffordshire Bull Terrier": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 40,
    coldTolerance: 30,
    dogFriendliness: 60,
    peopleFriendliness: 80,
    trainability: 55,
    preyDrive: 50,
    stamina: 60,
  },
  "Standard Schnauzer": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 40,
    coldTolerance: 55,
    dogFriendliness: 50,
    peopleFriendliness: 55,
    trainability: 65,
    preyDrive: 55,
    stamina: 60,
  },
  "Sussex Spaniel": {
    size: "medium",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 35,
    coldTolerance: 45,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 50,
    preyDrive: 60,
    stamina: 40,
  },
  "Swedish Lapphund": {
    size: "medium",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 20,
    coldTolerance: 85,
    dogFriendliness: 60,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 40,
    stamina: 55,
  },
  "Swedish Vallhund": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 30,
    coldTolerance: 65,
    dogFriendliness: 60,
    peopleFriendliness: 65,
    trainability: 65,
    preyDrive: 50,
    stamina: 60,
  },
  "Taiwan Dog": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 65,
    coldTolerance: 30,
    dogFriendliness: 35,
    peopleFriendliness: 35,
    trainability: 45,
    preyDrive: 65,
    stamina: 60,
  },
  "Teddy Roosevelt Terrier": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 75,
    stamina: 50,
  },
  "Terrier Mix": {
    size: "small",
    energyLevel: 65,
    exerciseNeeds: 55,
    heatTolerance: 45,
    coldTolerance: 35,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 50,
    preyDrive: 55,
    stamina: 45,
  },
  "Thai Bangkaew": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 70,
    coldTolerance: 25,
    dogFriendliness: 40,
    peopleFriendliness: 45,
    trainability: 45,
    preyDrive: 65,
    stamina: 60,
  },
  "Thai Ridgeback": {
    size: "medium",
    energyLevel: 65,
    exerciseNeeds: 60,
    heatTolerance: 75,
    coldTolerance: 15,
    dogFriendliness: 25,
    peopleFriendliness: 30,
    trainability: 35,
    preyDrive: 75,
    stamina: 60,
  },
  "Tibetan Mastiff": {
    size: "giant",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 15,
    coldTolerance: 90,
    dogFriendliness: 20,
    peopleFriendliness: 20,
    trainability: 25,
    preyDrive: 65,
    stamina: 45,
  },
  "Tibetan Spaniel": {
    size: "small",
    energyLevel: 35,
    exerciseNeeds: 30,
    heatTolerance: 30,
    coldTolerance: 40,
    dogFriendliness: 50,
    peopleFriendliness: 55,
    trainability: 45,
    preyDrive: 25,
    stamina: 25,
  },
  "Tibetan Terrier": {
    size: "medium",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 30,
    coldTolerance: 60,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 55,
    preyDrive: 30,
    stamina: 50,
  },
  "Tornjak": {
    size: "large",
    energyLevel: 50,
    exerciseNeeds: 45,
    heatTolerance: 25,
    coldTolerance: 80,
    dogFriendliness: 40,
    peopleFriendliness: 35,
    trainability: 40,
    preyDrive: 60,
    stamina: 50,
  },
  "Tosa": {
    size: "giant",
    energyLevel: 45,
    exerciseNeeds: 40,
    heatTolerance: 40,
    coldTolerance: 30,
    dogFriendliness: 20,
    peopleFriendliness: 30,
    trainability: 40,
    preyDrive: 55,
    stamina: 40,
  },
  "Toy Fox Terrier": {
    size: "tiny",
    energyLevel: 70,
    exerciseNeeds: 50,
    heatTolerance: 40,
    coldTolerance: 15,
    dogFriendliness: 50,
    peopleFriendliness: 65,
    trainability: 65,
    preyDrive: 55,
    stamina: 30,
  },
  "Transylvanian Hound": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 40,
    coldTolerance: 60,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 50,
    preyDrive: 85,
    stamina: 70,
  },
  "Treeing Tennessee Brindle": {
    size: "medium",
    energyLevel: 75,
    exerciseNeeds: 70,
    heatTolerance: 50,
    coldTolerance: 40,
    dogFriendliness: 60,
    peopleFriendliness: 60,
    trainability: 40,
    preyDrive: 85,
    stamina: 70,
  },
  "Treeing Walker Coonhound": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 80,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 65,
    peopleFriendliness: 65,
    trainability: 35,
    preyDrive: 95,
    stamina: 80,
  },
  "Valencian Terrier": {
    size: "small",
    energyLevel: 70,
    exerciseNeeds: 60,
    heatTolerance: 65,
    coldTolerance: 25,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 70,
    stamina: 50,
  },
  "Village Dog": {
    size: "medium",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 55,
    coldTolerance: 45,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 40,
    preyDrive: 55,
    stamina: 55,
  },
  "Vizsla": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 85,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 70,
    peopleFriendliness: 80,
    trainability: 75,
    preyDrive: 65,
    stamina: 80,
  },
  "Volpino Italiano": {
    size: "tiny",
    energyLevel: 60,
    exerciseNeeds: 45,
    heatTolerance: 35,
    coldTolerance: 30,
    dogFriendliness: 55,
    peopleFriendliness: 60,
    trainability: 50,
    preyDrive: 30,
    stamina: 25,
  },
  "Weimaraner": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 85,
    heatTolerance: 50,
    coldTolerance: 30,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 70,
    preyDrive: 70,
    stamina: 80,
  },
  "Welsh Springer Spaniel": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 70,
    peopleFriendliness: 75,
    trainability: 60,
    preyDrive: 55,
    stamina: 65,
  },
  "Welsh Terrier": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 45,
    coldTolerance: 50,
    dogFriendliness: 50,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 60,
    stamina: 55,
  },
  "West Highland White Terrier": {
    size: "small",
    energyLevel: 60,
    exerciseNeeds: 50,
    heatTolerance: 40,
    coldTolerance: 40,
    dogFriendliness: 55,
    peopleFriendliness: 70,
    trainability: 55,
    preyDrive: 60,
    stamina: 45,
  },
  "Wetterhoun": {
    size: "medium",
    energyLevel: 60,
    exerciseNeeds: 55,
    heatTolerance: 35,
    coldTolerance: 60,
    dogFriendliness: 50,
    peopleFriendliness: 50,
    trainability: 50,
    preyDrive: 55,
    stamina: 55,
  },
  "Whippet": {
    size: "medium",
    energyLevel: 50,
    exerciseNeeds: 45,
    heatTolerance: 45,
    coldTolerance: 15,
    dogFriendliness: 60,
    peopleFriendliness: 70,
    trainability: 50,
    preyDrive: 75,
    stamina: 40,
  },
  "Whippet Mix": {
    size: "medium",
    energyLevel: 55,
    exerciseNeeds: 50,
    heatTolerance: 48,
    coldTolerance: 20,
    dogFriendliness: 60,
    peopleFriendliness: 70,
    trainability: 52,
    preyDrive: 70,
    stamina: 45,
  },
  "Wire Fox Terrier": {
    size: "small",
    energyLevel: 75,
    exerciseNeeds: 65,
    heatTolerance: 50,
    coldTolerance: 35,
    dogFriendliness: 45,
    peopleFriendliness: 60,
    trainability: 55,
    preyDrive: 70,
    stamina: 55,
  },
  "Wirehaired Pointing Griffon": {
    size: "large",
    energyLevel: 80,
    exerciseNeeds: 75,
    heatTolerance: 45,
    coldTolerance: 55,
    dogFriendliness: 65,
    peopleFriendliness: 70,
    trainability: 65,
    preyDrive: 70,
    stamina: 75,
  },
  "Wirehaired Vizsla": {
    size: "large",
    energyLevel: 85,
    exerciseNeeds: 85,
    heatTolerance: 45,
    coldTolerance: 45,
    dogFriendliness: 70,
    peopleFriendliness: 80,
    trainability: 75,
    preyDrive: 65,
    stamina: 80,
  },
  "Working Kelpie": {
    size: "medium",
    energyLevel: 95,
    exerciseNeeds: 95,
    heatTolerance: 65,
    coldTolerance: 40,
    dogFriendliness: 45,
    peopleFriendliness: 40,
    trainability: 65,
    preyDrive: 65,
    stamina: 90,
  },
  "Xoloitzcuintli": {
    size: "medium",
    energyLevel: 55,
    exerciseNeeds: 45,
    heatTolerance: 70,
    coldTolerance: 10,
    dogFriendliness: 50,
    peopleFriendliness: 55,
    trainability: 45,
    preyDrive: 45,
    stamina: 45,
  },
  "Yakutian Laika": {
    size: "medium",
    energyLevel: 70,
    exerciseNeeds: 65,
    heatTolerance: 15,
    coldTolerance: 90,
    dogFriendliness: 55,
    peopleFriendliness: 55,
    trainability: 45,
    preyDrive: 60,
    stamina: 70,
  },
  "Yorkipoo": {
    size: "tiny",
    energyLevel: 55,
    exerciseNeeds: 40,
    heatTolerance: 28,
    coldTolerance: 15,
    dogFriendliness: 65,
    peopleFriendliness: 72,
    trainability: 58,
    preyDrive: 18,
    stamina: 25,
  },
  "Yorkshire Terrier": {
    size: "tiny",
    energyLevel: 55,
    exerciseNeeds: 35,
    heatTolerance: 25,
    coldTolerance: 10,
    dogFriendliness: 55,
    peopleFriendliness: 70,
    trainability: 45,
    preyDrive: 30,
    stamina: 20,
  },
  "Yorkshire Terrier Mix": {
    size: "tiny",
    energyLevel: 52,
    exerciseNeeds: 33,
    heatTolerance: 27,
    coldTolerance: 13,
    dogFriendliness: 58,
    peopleFriendliness: 70,
    trainability: 47,
    preyDrive: 28,
    stamina: 22,
  },
  "Yorktese": {
    size: "tiny",
    energyLevel: 40,
    exerciseNeeds: 28,
    heatTolerance: 22,
    coldTolerance: 12,
    dogFriendliness: 55,
    peopleFriendliness: 65,
    trainability: 40,
    preyDrive: 15,
    stamina: 18,
  },
};

export default dogBreeds;

/**
 * Map our internal DogSize to the API DogSize used in dog-profile types.
 * "tiny" → "XS", "small" → "S", "medium" → "M", "large" → "L", "giant" → "XL"
 */
export const BREED_SIZE_TO_API: Record<DogSize, string> = {
  tiny: 'XS',
  small: 'S',
  medium: 'M',
  large: 'L',
  giant: 'XL',
};

/**
 * API dog traits expected by the backend.
 */
export interface ApiDogTraits {
  dogTolerance: number;        // 1-100
  nervousAroundPeople: boolean;
  offleashReliability: number;  // 1-100
  stimulationTolerance: number; // 1-100
  highPreyDrive: boolean;
  walkingNeed: number;         // 1-100
  sensitiveToHeat: boolean;
}

/**
 * Convert a BreedProfile into the API dog traits.
 *
 * Mapping logic:
 *   dogFriendliness          → dogTolerance (1-100, direct)
 *   peopleFriendliness < 50  → nervousAroundPeople = true
 *   trainability             → offleashReliability (1-100, direct)
 *   avg(dogFriendliness, peopleFriendliness) → stimulationTolerance (1-100)
 *   preyDrive > 60           → highPreyDrive = true
 *   exerciseNeeds            → walkingNeed (1-100, direct)
 *   heatTolerance < 40        → sensitiveToHeat = true
 */
export function breedToTraits(profile: BreedProfile): ApiDogTraits {
  return {
    dogTolerance: profile.dogFriendliness,
    nervousAroundPeople: profile.peopleFriendliness < 50,
    offleashReliability: profile.trainability,
    stimulationTolerance: Math.round(
      (profile.dogFriendliness + profile.peopleFriendliness) / 2
    ),
    highPreyDrive: profile.preyDrive > 60,
    walkingNeed: profile.exerciseNeeds,
    sensitiveToHeat: profile.heatTolerance < 40,
  };
}

/**
 * Look up a breed by name and return both the profile and derived API traits.
 * Returns null if the breed name is not found.
 */
export function lookupBreed(breedName: string): {
  profile: BreedProfile;
  apiSize: string;
  traits: ApiDogTraits;
} | null {
  const profile = dogBreeds[breedName];
  if (!profile) return null;
  return {
    profile,
    apiSize: BREED_SIZE_TO_API[profile.size],
    traits: breedToTraits(profile),
  };
}

/**
 * Get a sorted list of all breed names for the picker.
 */
export function getBreedNames(): string[] {
  return Object.keys(dogBreeds).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );
}

/**
 * HOW TO USE THIS FOR TRAIL MATCHING
 *
 * Given a dog breed and a trail with traits, compute a compatibility score:
 *
 * 1. EXPOSURE MATCH:
 *    - High exposure (sunny) → penalize low heatTolerance breeds (brachycephalic, cold-climate)
 *    - Low exposure (shaded) → penalize low coldTolerance breeds in winter; otherwise neutral
 *    score_exposure = heatTolerance * (exposure / 100) + coldTolerance * (1 - exposure / 100)
 *
 * 2. DOG TRAFFIC MATCH:
 *    - High dogTraffic → need high dogFriendliness
 *    score_dogTraffic = dogFriendliness * (1 - dogTraffic / 100) + 50 * (dogTraffic / 100)
 *    i.e., if traffic is low, any dog is fine; if traffic is high, need friendliness
 *    Simplified: if dogTraffic > 50, penalize breeds with dogFriendliness < 50
 *
 * 3. FOOT TRAFFIC MATCH:
 *    - Similar logic with peopleFriendliness
 *
 * 4. OFF-LEASH MATCH:
 *    - If offLeash is true, need high trainability
 *    - If offLeash is false, trainability matters less
 *
 * 5. WILDLIFE MATCH:
 *    - If wildlife is true, need LOW preyDrive
 *    - If wildlife is false, preyDrive doesn't matter much
 *
 * 6. PATHS MATCH:
 *    - Narrow paths (high paths value) → penalize giant/large breeds
 *    - Wide open paths → any size is fine
 *
 * 7. STAMINA / DISTANCE MATCH:
 *    - Long trails (high distance) → need high stamina + exerciseNeeds
 *    - Short trails → any stamina is fine
 */